import { useSession } from "@clerk/clerk-react";
import { TerminalConnection } from "@modules/terminal-client";
import { useCallback, useState } from "react";
import { getTerminalServerUrl } from "@/hooks/useTerminalServerUrl";
import {
  getUnexpiredToken,
  useActiveMainCanvas,
  useAutoUpdateRef,
} from "@/utils/utils";

const claudeLoginRegex =
  // biome-ignore lint/suspicious/noControlCharactersInRegex: we are scanning terminal output
  /(https:\/\/claude\.ai\/oauth\/authorize\?code=[A-Za-z0-9-&_=\s%.+]+)\u001b/;

const TERMINAL_DATA_EVENT = "terminal-data";

class TerminalDataEvent extends Event {
  constructor(public readonly data: string) {
    super(TERMINAL_DATA_EVENT);
  }
}

interface TerminalEmitter extends EventTarget {
  addEventListener(
    type: typeof TERMINAL_DATA_EVENT,
    callback: (event: TerminalDataEvent) => void,
    options?: boolean | AddEventListenerOptions,
  ): void;
  removeEventListener(
    type: typeof TERMINAL_DATA_EVENT,
    callback: (event: TerminalDataEvent) => void,
    options?: boolean | EventListenerOptions,
  ): void;
}

const waitForCondition = async <T,>(
  emitter: TerminalEmitter,
  condition: (data: string) => T,
  signal?: AbortSignal,
) => {
  return new Promise<Exclude<T, undefined | null | "" | 0>>((resolve) => {
    const listener = (event: TerminalDataEvent) => {
      const result = condition(event.data);
      if (result) {
        emitter.removeEventListener(TERMINAL_DATA_EVENT, listener);
        resolve(result as Exclude<T, undefined | null | "" | 0>);
      }
    };
    emitter.addEventListener(TERMINAL_DATA_EVENT, listener, { signal });
  });
};

const submitAndWaitForCondition = async <T,>(
  connection: TerminalConnection,
  emitter: TerminalEmitter,
  condition: (data: string) => T,
  signal?: AbortSignal,
) => {
  const result = waitForCondition(emitter, condition, signal);
  connection.write("\r");
  return result;
};

export const useSetupClaudeCode = () => {
  const canvas = useActiveMainCanvas();
  const sessionRef = useAutoUpdateRef(useSession());
  const [state, setState] = useState<{
    connection: TerminalConnection;
    emitter: TerminalEmitter;
  }>();
  const startClaudeCodeSetup = useCallback(async () => {
    const decoder = new TextDecoder("utf-8");

    const emitter: TerminalEmitter = new EventTarget();

    const connection = new TerminalConnection({
      url: await getTerminalServerUrl(canvas.id, canvas.assigned_port, () =>
        getUnexpiredToken(sessionRef.current.session),
      ),
      createOptions: {
        cwd: "/app",
        shell: "/bin/bash",
        args: ["-l", "-c", "claude"],
      },
      onData: (data) => {
        const text = decoder.decode(data);
        emitter.dispatchEvent(new TerminalDataEvent(text));
      },
      autoReconnect: false,
    });
    const controller = new AbortController();
    const signal = controller.signal;

    const flow = Promise.race([
      waitForCondition(
        emitter,
        (data) => {
          return data.includes("? for shortcuts");
        },
        signal,
      ).then(() => ({ loggedIn: true, redirect: undefined }) as const),
      (async () => {
        await waitForCondition(
          emitter,
          (data) => data.includes("Dark mode"),
          signal,
        );
        await submitAndWaitForCondition(
          connection,
          emitter,
          (data) => data.includes("Select login method:"),
          signal,
        );
        const redirect = await submitAndWaitForCondition(
          connection,
          emitter,
          (data) => data.match(claudeLoginRegex)?.[1].replace(/\s+/g, ""),
          signal,
        );
        setState({ connection, emitter });
        return { loggedIn: false, redirect } as const;
      })(),
    ]);

    connection.connect();

    const result = await flow;

    if (!result)
      throw new Error("Assertion failed: Claude flow should have resolved");

    controller.abort();

    setState({ connection, emitter });

    return result;
  }, [canvas.assigned_port, canvas.id, sessionRef]);

  const sendCode = useCallback(
    async (code: string) => {
      if (!state) {
        console.error("Assertion failed: Connection should be established");
        return;
      }
      const { connection, emitter } = state;
      connection.write(code);
      await submitAndWaitForCondition(connection, emitter, (data) =>
        data.includes("Login successful"),
      );
      await submitAndWaitForCondition(connection, emitter, (data) =>
        data.includes("Security notes:"),
      );
      await submitAndWaitForCondition(
        connection,
        emitter,
        (data) =>
          data.includes("Yes, I trust this folder") ||
          data.includes("Yes, proceed"),
      );
      connection.write("\r");
      connection.write("\u0004");
      connection.write("\u0004");
      connection.dispose();
    },
    [state],
  );

  const cancelSetup = useCallback(() => {
    setState((state) => {
      state?.connection.dispose();
      return undefined;
    });
  }, []);

  return {
    startClaudeCodeSetup,
    sendCode,
    cancelSetup,
  };
};
